import { Fragment, useContext, useEffect, useRef, useState } from "react";
import ResultProps from "./ResultProps";
import { exportReport, getReportResultsV2 } from "../../../services/reportService";
import DemographicsReportResult from "../../../models/serviceModels/DemographicsReportResult";
import Loader from "../../UI/Loader";
import useElementVisible from "../../../hooks/useElementVisible";
import { getClientForm } from "../../../services/formsService";
import Form from "../../../models/serviceModels/Form";
import { formatJSONDateWithoutTime } from "../../../utils/dateHelpers";
import FormField from "../../../models/serviceModels/FormField";
import { ACTIVITY_DATE_ID, BREAK_LINE_ID, CHECKBOX_GROUP_ID, CLIENT_ID, DATE_ID, HEADER_ID, MULTI_LINE_TEXT_ID, NOTES_ID, RADIO_BUTTON_ID, SELECT_LIST_ID } from "../../../constants/fieldTypes";
import { format, parse } from "date-fns";
import TruncatedText from "../../UI/TruncatedText";
import ApiErrorMessages from "../../UI/ApiErrorMessages";
import './ReportResults.css';
import RelatedFormField from "../../../models/serviceModels/RelatedFormField";
import { Link } from "react-router-dom";
import Button from "../../UI/Button";
import { triggerDownloadOfUrl } from "../../../utils/reportHelpers";
import IconButton from "../../UI/IconButton";
import { scrollLeft, scrollRight } from "../../../utils/elementHelpers";
import { OrganizationContext } from "../../../App";
import { getDisplayGenderName, getDisplayRaceNames, getEthnicityFieldVisible, getRaceFieldLabel, isDemographicVisible } from "../../../utils/demographicsSchema";
import { householdIdentifiersEnabled, participantWorkedWithYearFilterEnabled, sortableReportResultsEnabled } from "../../../utils/features";
import DataTable from "../../UI/DataTable";
import { DataTableColumn, SortDirection } from "../../../models/DataTable";

const TAKE = 10;

const DemographicsResults = (props: ResultProps) => {
    const organization = useContext(OrganizationContext);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [clientFormLoading, setClientFormLoading] = useState(false);
    const [clientForm, setClientForm] = useState<Form>();
    const [totalCount, setTotalCount] = useState(0);
    const [results, setResults] = useState<DemographicsReportResult[]>([]);
    const [apiError, setApiError] = useState();
    const [skip, setSkip] = useState(0);
    const [paging, setPaging] = useState(false);
    const [reference, visible] = useElementVisible<HTMLDivElement>();
    const [exporting, setExporting] = useState(false);
    const [sortBy, setSortBy] = useState('firstName');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const loaded = useRef(false);

    useEffect(() => {
        setClientFormLoading(true);

        getClientForm().then(form => {
            setClientForm(form);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setClientFormLoading(false);
        });
    }, []);

    useEffect(() => {
        // make sure we reset this if the reportId changed
        if (loaded.current) {
            setSkip(0);
        }

        setResultsLoading(true);

        getReportResultsV2<DemographicsReportResult>({
            reportId: props.reportId,
            skip: 0,
            take: TAKE,
            sortBy,
            sortDirection: sortDirection === 'asc' ? 'Ascending' : 'Descending'
        }).then(r => {
            setResults(r.data);
            setTotalCount(r.totalCount);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setResultsLoading(false);
        });
    }, [props.reportId, props.refreshToken, sortBy, sortDirection]);

    useEffect(() => {
        if (!loaded.current || skip === 0) {
            return;
        }

        setPaging(true);
        setApiError(undefined);

        getReportResultsV2<DemographicsReportResult>({
            reportId: props.reportId,
            skip,
            take: TAKE,
            sortBy,
            sortDirection: sortDirection === 'asc' ? 'Ascending' : 'Descending'
        }).then(r => {
            setResults(er => {
                return [
                    ...er,
                    ...r.data
                ];
            });
            setTotalCount(r.totalCount);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setPaging(false);
        });
    }, [skip, sortBy, sortDirection]);

    useEffect(() => {
        if (visible && totalCount > 0) {
            setSkip(current => {
                return current + TAKE;
            });
        }
    }, [visible, totalCount]);

    useEffect(() => {
        loaded.current = true;
    }, []);

    const showGender = isDemographicVisible(organization.value, 'gender');
    const showEthnicity = getEthnicityFieldVisible(organization.value);
    const showRace = isDemographicVisible(organization.value, 'race');
    const showAddress = isDemographicVisible(organization.value, 'address');
    const showCity = isDemographicVisible(organization.value, 'city');
    const showState = isDemographicVisible(organization.value, 'state');
    const showZip = isDemographicVisible(organization.value, 'zip');
    const showPhone = isDemographicVisible(organization.value, 'phone');
    const showEmail = isDemographicVisible(organization.value, 'email');
    const raceFieldLabel = getRaceFieldLabel(organization.value).toLowerCase();
    const includesHouseholdMembers = props.parameters?.include_household_members === 'true';
    
    const handleOnExport = () => {
        setApiError(undefined);
        setExporting(true);
        exportReport({
            reportId: props.reportId
        }).then(r => {
            triggerDownloadOfUrl(r.downloadUrl);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setExporting(false);
        });
    };
    
    const handleScrollLeft = () => {
        scrollLeft('.report-results-table');
    };

    const handleScrollRight = () => {
        scrollRight('.report-results-table');
    };

    const getFieldValue = (field?: RelatedFormField): JSX.Element => {
        if (!field) {
            return <></>;
        }

        switch (field.fieldDataTypeId) {
            case ACTIVITY_DATE_ID:
            case DATE_ID:
                return <>{field.fieldValue && format(parse(field.fieldValue, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy')}</>;
            case CHECKBOX_GROUP_ID:
                return <>{JSON.parse(field.fieldValue || '[]').map((v: any) => v.label || v).join(', ')}</>;
            case SELECT_LIST_ID:
            case RADIO_BUTTON_ID:
                return <>{JSON.parse(field.fieldValue || '{}').label || ''}</>;
            case MULTI_LINE_TEXT_ID:
            case NOTES_ID:
                return <TruncatedText id={`${field.id}-truncated-field`} maxLength={20} value={field.fieldValue} />
            default:
                return <>{field.fieldValue}</>;
        }
    };

    const shouldNotBeFilteredOut = (field: FormField) => {
        return field.fieldTypeId !== HEADER_ID && field.fieldTypeId !== BREAK_LINE_ID && field.fieldTypeId !== CLIENT_ID;
    };

    const onSort = (key: string) => {
        setSkip(0);
        setSortDirection(current => sortBy === key && current === 'asc' ? 'desc' : 'asc');
        setSortBy(key);
    };

    const columns: DataTableColumn[] = [
        { key: 'firstName', label: 'name', sortable: true },
        ...(includesHouseholdMembers ? [{ key: 'recordType', label: 'record type', sortable: false }] : []),
        ...(householdIdentifiersEnabled ? [{ key: 'householdId', label: 'household id', sortable: false }] : []),
        { key: 'dateOfBirth', label: 'date of birth', sortable: true },
        ...(showGender ? [{ key: 'genderName', label: 'gender', sortable: false }] : []),
        ...(showEthnicity ? [{ key: 'ethnicityName', label: 'ethnicity', sortable: false }] : []),
        ...(showRace ? [{ key: 'raceName', label: raceFieldLabel, sortable: false }] : []),
        ...(showAddress ? [{ key: 'addressLine1', label: 'address line 1', sortable: false }, { key: 'addressLine2', label: 'address line 2', sortable: false }] : []),
        ...(showCity ? [{ key: 'addressCity', label: 'address city', sortable: false }] : []),
        ...(showState ? [{ key: 'addressState', label: 'address state', sortable: false }] : []),
        ...(showZip ? [{ key: 'addressZipCode', label: 'address zip code', sortable: false }] : []),
        ...(showPhone ? [{ key: 'phoneNumber', label: 'phone', sortable: true }] : []),
        ...(showEmail ? [{ key: 'email', label: 'email', sortable: true }] : []),
        { key: 'updated', label: 'updated', sortable: true },
        { key: 'created', label: 'created', sortable: true },
        ...(participantWorkedWithYearFilterEnabled ? [{ key: 'workedYears', label: 'worked years', sortable: false }] : []),
        ...(clientForm?.fields?.filter(shouldNotBeFilteredOut).map(f => ({ key: `field:${f.name}`, label: f.label, sortable: false })) || [])
    ];

    if ((organization.loading || clientFormLoading || resultsLoading) && !apiError) {
        return <Loader />
    }

    return <>
        <h3>Details for {includesHouseholdMembers ? 'Participant and Household Member' : 'Participant'} Demographics</h3>
        <ApiErrorMessages error={apiError} />
        {!clientFormLoading && !resultsLoading && <>
            
            <div className="row justify-content-between">
                <div className="column-auto">
                    <div className="row justify-content-between">
                        <div className="column-auto">
                            <IconButton id="scroll-left"
                                onClick={handleScrollLeft}
                                className="small flat"
                                icon={<i className="ri-arrow-left-s-line"></i>} />
                        </div>
                        <div className="column-auto">
                            <IconButton id="scroll-right"
                                onClick={handleScrollRight}
                                className="small flat"
                                icon={<i className="ri-arrow-right-s-line"></i>} />
                        </div>
                    </div>
                </div>
                <div className="column-auto">
                    <Button id="export-form-results"
                        text="Export Data"
                        onClick={handleOnExport}
                        loading={exporting}
                        disabled={exporting} />
                </div>
            </div>
            <div className="report-results-table" data-testid="report-results-table">
                <DataTable
                    columns={columns}
                    data={results}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={onSort}
                    sortingEnabled={sortableReportResultsEnabled}
                    emptyMessage="No results found"
                    renderRow={r => {
                            const nameParts = [r.firstName];
                            if (r.middleName) {
                                nameParts.push(r.middleName);
                            }
                            nameParts.push(r.lastName);

                            return (<Fragment key={r.id}><tr>
                                <td>{r.clientId ? <Link to={`/participants/${r.clientId}/profile-demographics`} target="_blank">{nameParts.join(' ')}</Link> : nameParts.join(' ')}</td>
                                {includesHouseholdMembers && <td>{r.isHouseholdMember ? 'Household Member' : 'Participant'}</td>}
                                {householdIdentifiersEnabled && <td>{r.householdId}</td>}
                                <td>{r.dateOfBirth && formatJSONDateWithoutTime(r.dateOfBirth, 'MM/dd/yyyy')}</td>
                                {showGender && <td>{getDisplayGenderName(r.genderName, organization.value)}</td>}
                                {showEthnicity && <td>{r.ethnicityName}</td>}
                                {showRace && <td>{getDisplayRaceNames({
                                    ethnicityName: r.ethnicityName,
                                    races: r.races.map(race => ({ name: race.raceName }))
                                }, organization.value).join(', ')}</td>}
                                {showAddress && <><td scope="col">{r.addressLine1}</td><td scope="col">{r.addressLine2}</td></>}
                                {showCity && <td scope="col">{r.addressCity}</td>}
                                {showState && <td scope="col">{r.addressState}</td>}
                                {showZip && <td scope="col">{r.addressZipCode}</td>}
                                {showPhone && <td scope="col">{r.phoneNumber}</td>}
                                {showEmail && <td scope="col">{r.email}</td>}
                                <td scope="col">{r.updated && format(new Date(r.updated), 'Pp')}</td>
                                <td scope="col">{r.created && format(new Date(r.created), 'Pp')}</td>
                                {participantWorkedWithYearFilterEnabled && <td scope="col">{r.workedYears}</td>}
                                {clientForm?.fields?.filter(shouldNotBeFilteredOut).map(f => {
                                    const matchingAdditionalInfoField = r.relatedFields.find(af => af.fieldName === f.name);
                                    return <td key={f.id} scope="col">{getFieldValue(matchingAdditionalInfoField)}</td>;
                                })}
                            </tr>{includesHouseholdMembers && (r.householdMembers || []).length > 0 && <tr className="sub-table-row"><td colSpan={columns.length}><div className="sub-table-container"><h4>{r.firstName} {r.lastName}'s Household Members ({r.householdMembers!.length})</h4><table className="table sub-table"><thead><tr><th>Name</th><th>Date of Birth</th></tr></thead><tbody>{r.householdMembers!.map(member => <tr key={member.id}><td>{member.clientId ? <Link to={`/participants/${member.clientId}/profile-demographics`} target="_blank">{[member.firstName, member.middleName, member.lastName].filter(Boolean).join(' ')}</Link> : [member.firstName, member.middleName, member.lastName].filter(Boolean).join(' ')}</td><td>{member.dateOfBirth && formatJSONDateWithoutTime(member.dateOfBirth, 'MM/dd/yyyy')}</td></tr>)}</tbody></table></div></td></tr>}</Fragment>);
                    }} />
            </div>
            {paging && <Loader />}
            {results && results.length < totalCount && <div ref={reference} className="padding-5 margin-bottom-5"></div>}
        </>
        }
    </>;
}

export default DemographicsResults;
